import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Skeleton } from "@shared-ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { GstCancelDialog } from "@/features/catalog/components/gst-cancel-dialog";
import {
	GstRateDialog,
	type GstRateDialogMode,
} from "@/features/catalog/components/gst-rate-dialog";
import { GstSignOffDialog } from "@/features/catalog/components/gst-sign-off-dialog";
import {
	formatRate,
	formatRateDay,
	GST_WRITE_PERMISSION,
	isWebUrl,
	STATE_LABEL,
	STATE_VARIANT,
} from "@/features/catalog/lib/gst-rates";
import {
	GST_RATES_KEY,
	SignOffCell,
} from "@/features/catalog/pages/gst-rates-dashboard";
import {
	type GstRate,
	getGstPrefixChain,
} from "@/features/catalog/services/gst-rates-api.service";
import { hasPermission } from "@/lib/permissions";

/**
 * One prefix's rate history, oldest first (#514, R3), and the actions that
 * belong to a row in context: cancel the latest row while it is scheduled,
 * correct it once it is in force, record a seeded row's sign-off.
 */
export function CatalogGstRatePrefixPage() {
	const { prefix = "" } = useParams();
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);
	const canWrite = hasPermission(operator, GST_WRITE_PERMISSION);
	const [dialog, setDialog] = useState<GstRateDialogMode | null>(null);
	const [cancelling, setCancelling] = useState<GstRate | null>(null);
	const [signing, setSigning] = useState<GstRate | null>(null);

	const chain = useQuery({
		queryKey: [...GST_RATES_KEY, "prefix", prefix],
		queryFn: () => getGstPrefixChain(prefix),
	});
	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: [...GST_RATES_KEY] });

	const rows = chain.data?.rows ?? [];
	const latestId = chain.data?.latestRateId ?? null;
	const liveBefore = (rate: GstRate): GstRate | null => {
		const live = rows.filter((row) => row.state !== "withdrawn");
		const index = live.findIndex((row) => row.id === rate.id);
		return index > 0 ? (live[index - 1] ?? null) : null;
	};

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
					<div className="grid gap-1.5">
						<Link className="text-sm underline" to="/catalog/gst-rates">
							All GST rates
						</Link>
						<CardTitle className="font-mono">{prefix}</CardTitle>
						<CardDescription>
							Oldest first. A withdrawn rate was entered wrong and corrected; it
							is kept here and never used to price.
						</CardDescription>
					</div>
					{canWrite ? (
						<Button onClick={() => setDialog({ kind: "add", prefix })}>
							Add a rate for {prefix}
						</Button>
					) : null}
				</CardHeader>
				<CardContent className="grid gap-3">
					{chain.isPending ? (
						<Skeleton className="h-32 w-full" />
					) : chain.isError ? (
						<div className="grid gap-3 rounded-2xl border border-dashed p-6 text-center">
							<p className="text-sm text-muted-foreground">
								Could not load the rates for {prefix}.
							</p>
							<div>
								<Button
									variant="outline"
									size="sm"
									onClick={() => chain.refetch()}
								>
									Try again
								</Button>
							</div>
						</div>
					) : rows.length === 0 ? (
						<EmptyState
							title={`No GST rate is on record for ${prefix}`}
							hint="A product whose code falls under this prefix shows no price until a rate is added here or under a shorter prefix."
						/>
					) : (
						rows.map((rate) => (
							<section
								key={rate.id}
								aria-label={`${prefix} at ${formatRate(rate.rateBp)} from ${formatRateDay(rate.effectiveFrom)}`}
								className={`grid gap-2 rounded-2xl border p-4 text-sm ${rate.state === "withdrawn" ? "bg-slate-50 text-muted-foreground" : ""}`}
							>
								<div className="flex flex-wrap items-center gap-3">
									<span className="text-lg font-semibold">
										{formatRate(rate.rateBp)}
									</span>
									<span>
										{formatRateDay(rate.effectiveFrom)} →{" "}
										{rate.effectiveTo
											? formatRateDay(rate.effectiveTo)
											: "open"}
									</span>
									<Badge variant={STATE_VARIANT[rate.state]}>
										{STATE_LABEL[rate.state]}
									</Badge>
								</div>
								<dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[max-content_1fr]">
									<dt className="text-muted-foreground">Notification</dt>
									<dd>{rate.source}</dd>
									<dt className="text-muted-foreground">S. No.</dt>
									<dd>{rate.notificationSerial ?? "Not recorded"}</dd>
									<dt className="text-muted-foreground">Sign-off</dt>
									<dd>
										<SignOffCell
											rate={rate}
											canWrite={canWrite}
											onRecord={setSigning}
										/>
									</dd>
									<dt className="text-muted-foreground">Evidence</dt>
									<dd>
										{isWebUrl(rate.evidenceUrl) ? (
											<a
												className="break-all underline"
												href={rate.evidenceUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												{rate.evidenceUrl}
											</a>
										) : (
											(rate.evidenceUrl ?? "None")
										)}
									</dd>
									<dt className="text-muted-foreground">Entered</dt>
									<dd>
										{rate.origin === "seed"
											? "By the GST seed"
											: `By ${rate.enteredBy ?? "an admin"}`}
									</dd>
									{rate.note ? (
										<>
											<dt className="text-muted-foreground">Note</dt>
											<dd className="whitespace-pre-wrap">{rate.note}</dd>
										</>
									) : null}
									{rate.withdrawnAt ? (
										<>
											<dt className="text-muted-foreground">Withdrawn</dt>
											<dd>
												{formatRateDay(rate.withdrawnAt)}
												{rate.withdrawnBy ? ` by ${rate.withdrawnBy}` : ""}:{" "}
												{rate.withdrawnReason}
											</dd>
										</>
									) : null}
								</dl>
								{canWrite && rate.id === latestId ? (
									<div className="flex justify-end">
										{rate.state === "scheduled" ? (
											<Button
												variant="outline"
												size="sm"
												onClick={() => setCancelling(rate)}
											>
												Cancel this rate
											</Button>
										) : rate.state === "in_force" ? (
											<Button
												variant="outline"
												size="sm"
												onClick={() => setDialog({ kind: "correct", rate })}
											>
												Correct this rate
											</Button>
										) : null}
									</div>
								) : null}
							</section>
						))
					)}
				</CardContent>
			</Card>

			{dialog ? (
				<GstRateDialog
					mode={dialog}
					onClose={() => setDialog(null)}
					onChanged={refresh}
				/>
			) : null}
			{cancelling ? (
				<GstCancelDialog
					rate={cancelling}
					previous={liveBefore(cancelling)}
					onClose={() => setCancelling(null)}
					onChanged={refresh}
				/>
			) : null}
			{signing ? (
				<GstSignOffDialog
					rate={signing}
					onClose={() => setSigning(null)}
					onChanged={refresh}
				/>
			) : null}
		</div>
	);
}
